"use client";

/**
 * Boots the PostHog browser client once (idempotent).
 *
 * No React context is needed — PostHog uses a global singleton.
 * We only render children; the init runs as a side-effect on mount.
 *
 * Also installs global error listeners that forward uncaught errors
 * and unhandled promise rejections to PostHog. These never throw and
 * never block the app; they're passive observers.
 *
 * Mount this inside your top-level client provider tree (e.g. inside
 * <Providers> in src/store/providers.jsx OR directly in RootLayout).
 */

import { useEffect } from "react";
import { initPosthog } from "./posthog-client";
import { captureException, track } from "./track";
import { EVENTS } from "./events";

export default function PostHogProvider({ children }) {
  useEffect(() => {
    // Fire-and-forget; safe/no-op when kill-switch is off.
    initPosthog();

    // Deduplicate identical errors within a short window so a
    // pathological render loop doesn't flood PostHog.
    const seen = new Map();
    const DUPE_WINDOW_MS = 5000;
    const isIgnorableTransportError = (errLike) => {
      const name = (errLike?.name || "").toLowerCase();
      const type = (errLike?.type || "").toLowerCase();
      const message = `${errLike?.message || ""} ${errLike?.error ||
        ""}`.toLowerCase();
      return (
        name.includes("transportconnectionerror") ||
        name.includes("retryerror") ||
        type.includes("transportconnectionerror") ||
        message.includes("socket is not connected") ||
        message.includes("unreachable hosts") ||
        message.includes("support-unreachable-hosts") ||
        message.includes("application id may be incorrect")
      );
    };
    const isIgnorableRealtimeStageHydrationError = (errLike) => {
      const message = `${errLike?.message || ""} ${errLike?.error ||
        ""}`.toLowerCase();
      const stack = `${errLike?.stack || ""}`.toLowerCase();
      const isNullPropertyRead =
        (message.includes("cannot read properties of null") ||
          message.includes("is not an object")) &&
        (message.includes("reading 'stage'") ||
          message.includes("reading 'chat'") ||
          message.includes("evaluating 'e.stage'") ||
          message.includes("evaluating 'e.chat'"));
      return (
        isNullPropertyRead &&
        (stack.includes("this.meeting.stage.join") ||
          stack.includes("stageStatusUpdate") ||
          stack.includes("activejoinstage") ||
          stack.includes("disconnectmeeting") ||
          stack.includes("disconnectedcallback"))
      );
    };
    // RealtimeKit polls WebRTC stats (getStats/getProducersReport) on an
    // interval that isn't cleared before the peer connection closes, so a
    // poll in flight during/after leave throws InvalidStateError: closed.
    const isIgnorableRealtimeStatsPollingError = (errLike) => {
      const name = (errLike?.name || "").toLowerCase();
      const message = `${errLike?.message || ""} ${errLike?.error ||
        ""}`.toLowerCase();
      const stack = `${errLike?.stack || ""}`.toLowerCase();
      const isClosedStateError =
        name.includes("invalidstateerror") ||
        (message.includes("invalidstateerror") && message.includes("closed")) ||
        message.trim() === "closed";
      if (!isClosedStateError) return false;
      return (
        stack.includes("realtimekit") ||
        stack.includes("getproducersreport") ||
        stack.includes("generateproducerstreammap") ||
        stack.includes("getproducerstatsfromreport") ||
        stack.includes("parsertcreport")
      );
    };
    const isIgnorableTelemetryNoise = (errLike) =>
      isIgnorableTransportError(errLike) ||
      isIgnorableRealtimeStageHydrationError(errLike) ||
      isIgnorableRealtimeStatsPollingError(errLike);
    const isIgnorableBrowserEventNoise = (eventLike) => {
      if (!(eventLike instanceof Event)) return false;
      const targetTag =
        eventLike.target && "tagName" in eventLike.target
          ? String(eventLike.target.tagName || "").toUpperCase()
          : "";

      // Browsers can surface resource load failures as trusted Event objects
      // (for example, LINK stylesheet/network hiccups) without actionable stacks.
      return eventLike.isTrusted === true && targetTag === "LINK";
    };
    const isDuplicate = (key) => {
      const now = Date.now();
      const prev = seen.get(key);
      seen.set(key, now);
      // Prune old entries occasionally
      if (seen.size > 50) {
        for (const [k, t] of seen) {
          if (now - t > DUPE_WINDOW_MS) seen.delete(k);
        }
      }
      return prev && now - prev < DUPE_WINDOW_MS;
    };

    // Wrap console.error in production so every `console.error(...)` call
    // becomes a searchable PostHog event. Dev keeps the original behavior
    // so stack traces stay visible in DevTools.
    let originalConsoleError = null;
    if (
      process.env.NODE_ENV === "production" &&
      typeof console !== "undefined"
    ) {
      originalConsoleError = console.error;
      console.error = function patchedConsoleError(...args) {
        try {
          const firstErr = args.find((a) => a instanceof Error);
          if (
            isIgnorableTelemetryNoise(firstErr) ||
            args.some(
              (a) => a && typeof a === "object" && isIgnorableTelemetryNoise(a),
            ) ||
            (firstErr == null &&
              args.some(
                (a) =>
                  typeof a === "string" &&
                  isIgnorableTelemetryNoise({ message: a }),
              ))
          ) {
            return originalConsoleError.apply(console, args);
          }
          const message = args
            .map((a) => {
              if (a instanceof Error) return a.message;
              if (typeof a === "string") return a;
              try {
                return JSON.stringify(a);
              } catch {
                return String(a);
              }
            })
            .join(" ")
            .slice(0, 500);
          const key = `console.error:${message.slice(0, 100)}`;
          if (!isDuplicate(key)) {
            if (firstErr) {
              captureException(firstErr, {
                source: "console.error",
                message,
              });
            }
            track(EVENTS.CLIENT_ERROR, {
              message,
              name: firstErr?.name || "ConsoleError",
              source: "console.error",
            });
          }
        } catch {
          /* never throw from the console wrapper */
        }
        return originalConsoleError.apply(console, args);
      };
    }

    const handleError = (event) => {
      try {
        if (isIgnorableBrowserEventNoise(event)) return;
        const err = event?.error || new Error(event?.message || "unknown");
        if (isIgnorableTelemetryNoise(err)) return;
        const key = `${err?.name || "Error"}:${event?.message ||
          err?.message}:${event?.filename}:${event?.lineno}`;
        if (isDuplicate(key)) return;
        captureException(err, {
          source: "window.onerror",
          filename: event?.filename,
          lineno: event?.lineno,
          colno: event?.colno,
          pathname:
            typeof window !== "undefined"
              ? window.location.pathname
              : undefined,
        });
        track(EVENTS.CLIENT_ERROR, {
          message: (event?.message || err?.message || "").slice(0, 300),
          name: err?.name || "Error",
          filename: event?.filename,
          lineno: event?.lineno,
        });
      } catch {
        /* never throw from error handler */
      }
    };

    const handleRejection = (event) => {
      try {
        const reason = event?.reason;
        if (isIgnorableBrowserEventNoise(reason)) return;
        const err =
          reason instanceof Error
            ? reason
            : new Error(
                typeof reason === "string"
                  ? reason
                  : reason instanceof Event
                  ? JSON.stringify({
                      type: reason.type,
                      isTrusted: reason.isTrusted,
                      target: reason.target?.tagName,
                      currentTarget: reason.currentTarget?.tagName,
                    }).slice(0, 300)
                  : JSON.stringify(reason || {}).slice(0, 300),
              );
        if (isIgnorableTelemetryNoise(err) || isIgnorableTelemetryNoise(reason))
          return;
        const key = `unhandledrejection:${err?.name}:${err?.message}`;
        if (isDuplicate(key)) return;
        captureException(err, {
          source: "unhandledrejection",
          pathname:
            typeof window !== "undefined"
              ? window.location.pathname
              : undefined,
        });
        track(EVENTS.CLIENT_ERROR, {
          message: (err?.message || "unhandled promise rejection").slice(
            0,
            300,
          ),
          name: err?.name || "UnhandledRejection",
        });
      } catch {
        /* never throw from error handler */
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
      // Restore original console.error if we wrapped it (prod only)
      if (originalConsoleError) {
        console.error = originalConsoleError;
      }
    };
  }, []);

  return children;
}
